import { useRef, useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Linkedin, Mail } from 'lucide-react';

const TeamSection = () => {
  const { language } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [hoveredMember, setHoveredMember] = useState(null);

  const teamMembers = [
    {
      name: 'Léa',
      role: language === 'fr' ? 'Directrice de l\'agence' : 'Agency Director',
      description: language === 'fr'
        ? 'Co-fondatrice de BAO AGENCY, Léa orchestre la vision créative et éditoriale de l\'agence. Elle accompagne chaque client avec exigence et sensibilité, en veillant à ce que chaque projet reflète parfaitement l\'identité et les ambitions de la marque.'
        : 'Co-founder of BAO AGENCY, Léa orchestrates the creative and editorial vision of the agency. She supports each client with rigor and sensitivity, ensuring that every project perfectly reflects the brand\'s identity and ambitions.',
      image: 'https://randomuser.me/api/portraits/women/44.jpg',
    },
    {
      name: 'Ritchi',
      role: language === 'fr' ? 'Directeur de l\'agence' : 'Agency Director',
      description: language === 'fr'
        ? 'Co-fondateur de BAO AGENCY, Ritchi pilote la stratégie globale et le développement de l\'agence. De la conception à l\'exécution, il s\'assure que chaque projet dépasse les attentes des clients et génère un impact réel et mesurable.'
        : 'Co-founder of BAO AGENCY, Ritchi drives the overall strategy and development of the agency. From conception to execution, he ensures every project exceeds client expectations and generates real, measurable impact.',
      image: 'https://randomuser.me/api/portraits/men/32.jpg',
    },
  ];

  // IntersectionObserver for team member animations
  useEffect(() => {
    const members = document.querySelectorAll('.team-member');
    
    const observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          setTimeout(function() {
            entry.target.classList.add('is-visible');
          }, Array.from(members).indexOf(entry.target) * 200);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.2 });

    members.forEach(function(member) {
      observer.observe(member);
    });

    return () => {
      members.forEach(function(member) {
        observer.unobserve(member);
      });
    };
  }, []);

  return (
    <section
      id="team"
      ref={ref}
      className="py-24 md:py-32 bg-neutral-50"
      data-testid="team-section"
    >
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center mb-16 md:mb-20">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            className="inline-block text-xs font-semibold tracking-[0.3em] uppercase text-neutral-500 mb-4"
          >
            {language === 'fr' ? 'Les visages derrière BAO' : 'The faces behind BAO'}
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.1 }}
            className="heading-xl text-4xl md:text-5xl lg:text-6xl mb-4 text-black"
            data-testid="team-title"
          >
            {language === 'fr' ? 'Notre équipe' : 'Our team'}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
            className="text-lg text-neutral-600 max-w-2xl mx-auto"
          >
            {language === 'fr'
              ? 'Des spécialistes du digital au service de votre marque.'
              : 'Digital specialists at the service of your brand.'}
          </motion.p>
        </div>

        {/* Team Grid - Using CSS animations with IntersectionObserver */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10 max-w-4xl mx-auto">
          {teamMembers.map((member, index) => (
            <div
              key={index}
              className="team-member group bg-white rounded-2xl overflow-hidden shadow-lg"
              data-testid={`team-member-${index}`}
              onMouseEnter={() => setHoveredMember(index)}
              onMouseLeave={() => setHoveredMember(null)}
            >
              {/* Photo */}
              <div className="relative aspect-[4/5] overflow-hidden">
                <img
                  src={member.image}
                  alt={member.name}
                  className={`w-full h-full object-cover transition-transform duration-400 ${
                    hoveredMember === index ? 'scale-105' : 'scale-100'
                  }`}
                />
                
                {/* Overlay on hover */}
                <div
                  className={`absolute inset-0 bg-black/60 flex items-center justify-center gap-4 transition-opacity duration-300 ${
                    hoveredMember === index ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <a
                    href="#"
                    className="w-12 h-12 bg-white rounded-full flex items-center justify-center hover:scale-110 transition-transform"
                  >
                    <Linkedin className="w-5 h-5 text-black" />
                  </a>
                  <a
                    href="#"
                    className="w-12 h-12 bg-white rounded-full flex items-center justify-center hover:scale-110 transition-transform"
                  >
                    <Mail className="w-5 h-5 text-black" />
                  </a>
                </div>
              </div>

              {/* Info */}
              <div className="p-6 md:p-8">
                <h3 className="font-serif text-2xl font-bold text-black mb-1">
                  {member.name}
                </h3>
                <p className="text-sm font-semibold text-neutral-500 uppercase tracking-wider mb-4">
                  {member.role}
                </p>
                <p className="text-neutral-600 leading-relaxed">
                  {member.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TeamSection;
